import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {issueAttributesType, oxygenUserType} from '../../constants/proptype-validation';
import {canAddComment} from '../../utils/issues-helpers';
import IssuePreviewActivityContentContainer from './IssuePreviewActivityContentContainer';
import i18n from '../../utils/i18n';
import CommentBox from '@adsk/bim360-matrix-react-comment-box';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import Loader from '@adsk/bim360-matrix-react-loader';
import {ActivityBatchReadTypes} from '../../constants/activity';

const IssuePreviewActivityTab = ({
  issue,
  isLoading,
  fetchActivities,
  activityBlocks,
  currentUser,
  createCommentAction,
  disableKeyboard,
  groupedProjectEntitiesForMention,
  saveActivityCommentEditorState,
  activityCommentEditorState,
  activityServiceErrored,
  container,
  contentElementRef
}) => {
  const [commentBoxFocused, setCommentBoxFocused] = useState(false);

  useEffect(() => {
    fetchActivities({
      issueId: issue.id,
      readType: ActivityBatchReadTypes.LATEST
    });
  }, [fetchActivities, issue.id]);

  if ((isLoading && activityBlocks.length === 0) || !currentUser) {
    return <Loader />;
  }

  if (activityServiceErrored) {
    return (
      <div className="IssuePreviewActivityTab__empty-state">
        <EmptyState
          icon={EmptyState.Icons.CONE}
          text={i18n.t('activity.error')}
          footer={<div
            className="IssuePreviewActivityTab__activity-error-try-again"
            onClick={() =>
              fetchActivities({
                issueId: issue.id,
                readType: ActivityBatchReadTypes.LATEST
              })}
          >
            {i18n.t('activity.tryAgain')}
          </div>}
        />
      </div>
    );
  }

  return (
    <div className="IssuePreviewActivityTab" data-testid="IssuePreviewActivityTab">
      <div className="IssuePreviewActivityTabActivityList">
        <IssuePreviewActivityContentContainer
          activityBlocks={activityBlocks}
          container={container}
          contentElementRef={contentElementRef}
        />
        {canAddComment(issue) && (
          <div className="IssuePreviewActivityTab__footer" data-testid="IssuePreviewActivityTab__footer">
            <CommentBox
              className="IssuePreviewActivityTab__comment-box"
              inputClassName="IssuePreviewActivityTab__comment-box-input"
              data-testid="IssuePreviewActivityTab__comment-box-input"
              currentUser={currentUser}
              suggestions={groupedProjectEntitiesForMention}
              editorState={activityCommentEditorState}
              onFocus={() => {
                disableKeyboard(true);
                setCommentBoxFocused(true);
              }}
              onBlur={() => {
                setCommentBoxFocused(false);
                disableKeyboard(false);
              }}
              onChange={(text, commentEditorState) => {
                saveActivityCommentEditorState(commentEditorState);
              }}
              onKeyUp={event => {
                // Since the Modal is attaching to the 'keyup' event of the body,
                // and we're going to call "disableKeyboard(false)" right now,
                // the modal event handler (that's going to be invoked after this handler) is going to think it needs to handle the keyboard.
                // That's why we need to stopPropagation and preventDefault right now to prevent the modal from handling this event.
                if (event.keyCode === 27 && !commentBoxFocused) {
                  event.stopPropagation();
                  event.preventDefault();
                  disableKeyboard(false);
                }
              }}
              onSend={commentText =>
                new Promise((resolve, reject) => createCommentAction(issue.id, commentText, resolve, reject))}
              getSuggestionId={user => user.oxygenId}
              getSuggestionName={user => user.name}
              getMentionId={user => user.oxygenId}
              getMentionName={user => user.name}
              getMentionType={user => user.oxygenType}
            />
          </div>
        )}
      </div>
    </div>
  );
};

IssuePreviewActivityTab.displayName = 'IssuePreviewActivityTab';

IssuePreviewActivityTab.propTypes = {
  issue: issueAttributesType,
  isLoading: PropTypes.bool,
  fetchActivities: PropTypes.func.isRequired,
  fetchNewActivities: PropTypes.func.isRequired,
  activityBlocks: PropTypes.array,
  currentUser: oxygenUserType,
  createCommentAction: PropTypes.func.isRequired,
  disableKeyboard: PropTypes.func.isRequired,
  groupedProjectEntitiesForMention: PropTypes.array.isRequired,
  saveActivityCommentEditorState: PropTypes.func,
  activityCommentEditorState: PropTypes.object,
  activityServiceErrored: PropTypes.bool,
  container: PropTypes.object,
  contentElementRef: PropTypes.object
};

export default IssuePreviewActivityTab;
